// What the dashboard's stacks are found among (`poolOf`), and the native
// stack members no section loaded, read by ID to show with them.
import { DASH_PAGE, type DashPr } from '../../api/queries';
import { memberKey, unloadedMembers, type UnloadedMember } from '../../logic/stacks';
import type { KnownAttention } from './attention';
import { rowOf, type DashRow } from './rows';
import type { RowRead } from './search';

/** The native stack members no section loaded, as last read by ID. */
export interface Outside {
  readonly prs: readonly DashPr[];
  /** Those GitHub does not show the viewer (read by ID, they came back as nothing), by `memberKey`. */
  readonly hidden: ReadonlySet<string>;
  /** Those whose read needs SSO authorization, by `memberKey`: not saved, and read again on every load. */
  readonly sso: ReadonlySet<string>;
}

export const noOutside: Outside = { prs: [], hidden: new Set(), sso: new Set() };

/**
 * Native stack members no section loaded read per load, in one request
 * (`DashboardCapabilities.rows`); members past these show as numbers.
 */
export const OUTSIDE_MAX = DASH_PAGE;

/**
 * The pool from every section's latest rows: one row per pull request, its
 * most recently updated copy, with its attention as known; then the
 * members of `outside` no section loaded, marked `outside`.
 */
export function poolOf(loaded: readonly (readonly DashPr[] | undefined)[], outside: readonly DashPr[], known: KnownAttention): DashRow[] {
  const newest = new Map<string, DashPr>();
  for (const prs of loaded)
    for (const pr of prs ?? []) {
      const seen = newest.get(pr.id);
      if (!seen || pr.updatedAt > seen.updatedAt) newest.set(pr.id, pr);
    }
  const rest = outside.filter((pr) => !newest.has(pr.id)).map((pr): DashRow => ({ ...rowOf(pr, known), outside: true }));
  return [...[...newest.values()].map((pr) => rowOf(pr, known)), ...rest];
}

/** The members to read by ID, and the earlier reads to keep (see `readOutside`). */
export interface OutsidePlan {
  kept: DashPr[];
  todo: UnloadedMember[];
}

/**
 * Which native stack members that no section loaded but that a loaded
 * member's `entries` names by ID to read, up to OUTSIDE_MAX: `all` reads
 * every one again (a load); otherwise only those not read yet or that
 * needed SSO (a saved copy shown, a section read again). Members no longer
 * named are dropped.
 */
export function planOutside(loaded: readonly (readonly DashPr[] | undefined)[], outside: Outside, all: boolean): OutsidePlan {
  const want = unloadedMembers(loaded.flatMap((prs) => prs ?? []));
  const wanted = new Set(want.map((m) => m.id));
  const kept = all ? [] : outside.prs.filter((p) => wanted.has(p.id));
  const have = new Set(kept.map((p) => p.id));
  const todo = want.filter((m) => !have.has(m.id) && (all || !outside.hidden.has(memberKey(m.repo, m.number)))).slice(0, OUTSIDE_MAX);
  return { kept, todo };
}

/**
 * The members after reading `plan.todo` (`got`, in order): those read join
 * the kept ones, those GitHub does not show the viewer join `hidden`, and
 * those whose read needs SSO authorization join `sso`. Null when nothing
 * changed.
 */
export function applyOutside(outside: Outside, plan: OutsidePlan, got: RowRead[], all: boolean): Outside | null {
  const found = new Map(got.flatMap((p) => (p && p !== 'sso' ? [[p.id, p] as const] : [])));
  const hidden = new Set(all ? [] : [...outside.hidden]);
  const sso = new Set(all ? [] : [...outside.sso]);
  plan.todo.forEach((m, i) => {
    const key = memberKey(m.repo, m.number);
    sso.delete(key);
    if (got[i] === 'sso') sso.add(key);
    else if (!found.has(m.id) && i < got.length) hidden.add(key);
  });
  const prs = [...plan.kept, ...found.values()];
  if (!plan.todo.length && prs.length === outside.prs.length) return null;
  return { prs, hidden, sso };
}
